import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  Autocomplete,
  Chip
} from '@mui/material';
import { adminService } from '../../services/adminService';

const PlatformSettingsModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    site_name: '',
    site_description: '',
    max_products_for_users_without_boutique: 5,
    free_plan_days: 30,
    subscription_duration_days: 30,
    boutique_specialties: [],
    contact_email: '',
    contact_phone: '',
    contact_whatsapp: '',
    contact_address: '',
    contact_opening_hours: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Charger les données existantes quand la modal s'ouvre
  useEffect(() => {
    if (open) {
      loadCurrentSettings();
    }
  }, [open]);

  const loadCurrentSettings = async () => {
    try {
      const data = await adminService.getSystemSettings();
      if (data && data.settings) {
        setFormData({
          site_name: data.settings.site_name || '',
          site_description: data.settings.site_description || '',
          max_products_for_users_without_boutique: data.settings.max_products_for_users_without_boutique || 5,
          free_plan_days: data.settings.free_plan_days || 30,
          subscription_duration_days: data.settings.subscription_duration_days || 30,
          boutique_specialties: Array.isArray(data.settings.boutique_specialties) ? data.settings.boutique_specialties : [],
          contact_email: data.settings.contact_email || '',
          contact_phone: data.settings.contact_phone || '',
          contact_whatsapp: data.settings.contact_whatsapp || '',
          contact_address: data.settings.contact_address || '',
          contact_opening_hours: data.settings.contact_opening_hours || ''
        });
      }
      
      // Charger aussi les spécialités depuis l'API
      try {
        const specialtiesResponse = await adminService.getSystemSetting('boutique_specialties');
        if (specialtiesResponse && specialtiesResponse.value && Array.isArray(specialtiesResponse.value)) {
          setFormData(prev => ({
            ...prev,
            boutique_specialties: specialtiesResponse.value
          }));
        }
      } catch (err) {
        console.error('Erreur lors du chargement des spécialités:', err);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.site_name.trim()) {
      setError('Le nom de la plateforme est requis');
      return;
    }

    const maxProducts = parseInt(formData.max_products_for_users_without_boutique);
    if (isNaN(maxProducts) || maxProducts < 0) {
      setError('Le nombre maximum de produits doit être un nombre positif');
      return;
    }

    const freePlanDays = parseInt(formData.free_plan_days);
    if (isNaN(freePlanDays) || freePlanDays < 1) {
      setError('Le nombre de jours du plan free doit être un nombre positif supérieur à 0');
      return;
    }

    const subscriptionDurationDays = parseInt(formData.subscription_duration_days);
    if (isNaN(subscriptionDurationDays) || subscriptionDurationDays < 1) {
      setError('La durée d\'abonnement doit être un nombre positif supérieur à 0');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Sauvegarder les paramètres
      await adminService.updateSystemSetting('site_name', formData.site_name);
      await adminService.updateSystemSetting('site_description', formData.site_description);
      await adminService.updateSystemSetting('max_products_for_users_without_boutique', parseInt(formData.max_products_for_users_without_boutique));
      await adminService.updateSystemSetting('free_plan_days', parseInt(formData.free_plan_days));
      await adminService.updateSystemSetting('subscription_duration_days', parseInt(formData.subscription_duration_days));
      await adminService.updateSystemSetting('boutique_specialties', formData.boutique_specialties);
      await adminService.updateSystemSetting('contact_email', formData.contact_email);
      await adminService.updateSystemSetting('contact_phone', formData.contact_phone);
      await adminService.updateSystemSetting('contact_whatsapp', formData.contact_whatsapp);
      await adminService.updateSystemSetting('contact_address', formData.contact_address);
      await adminService.updateSystemSetting('contact_opening_hours', formData.contact_opening_hours);

      onSuccess('Paramètres mis à jour avec succès');
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde des paramètres');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Paramètres de la Plateforme</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              name="site_name"
              label="Nom de la plateforme"
              value={formData.site_name}
              onChange={handleChange}
              fullWidth
              required
              helperText="Le nom apparaîtra dans la navbar et le footer"
            />
            
            <TextField
              name="site_description"
              label="Description de la plateforme"
              value={formData.site_description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              helperText="La description apparaîtra dans le footer et les métadonnées"
            />
            
            <TextField
              name="max_products_for_users_without_boutique"
              label="Nombre maximum de produits pour utilisateurs sans boutique"
              type="number"
              value={formData.max_products_for_users_without_boutique}
              onChange={handleChange}
              fullWidth
              required
              inputProps={{ min: 0, step: 1 }}
              helperText="Nombre maximum de produits qu'un utilisateur sans boutique peut créer"
            />
            
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>
                Paramètres d'Abonnement
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configurez les paramètres liés aux abonnements
              </Typography>
            </Box>
            
            <TextField
              name="free_plan_days"
              label="Nombre de jours du plan gratuit"
              type="number"
              value={formData.free_plan_days}
              onChange={handleChange}
              fullWidth
              required
              inputProps={{ min: 1, step: 1 }}
              helperText="Durée en jours du plan gratuit offert aux nouveaux utilisateurs (par défaut: 30 jours)"
            />
            
            <TextField
              name="subscription_duration_days"
              label="Durée d'un abonnement (en jours)"
              type="number"
              value={formData.subscription_duration_days}
              onChange={handleChange}
              fullWidth
              required
              inputProps={{ min: 1, step: 1 }}
              helperText="Durée en jours d'un abonnement payant (par défaut: 30 jours)"
            />
            
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formData.boutique_specialties || []}
              onChange={(event, newValue) => {
                setFormData(prev => ({ ...prev, boutique_specialties: newValue }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Spécialités de boutique"
                  placeholder="Ajoutez des spécialités disponibles pour les boutiques"
                  helperText="Ces spécialités seront disponibles lors de la création/modification de boutiques. Appuyez sur Entrée pour ajouter."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={index}
                  />
                ))
              }
            />
            
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>
                Informations de Contact
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ces informations seront affichées sur la page de contact
              </Typography>
            </Box>
            
            <TextField
              name="contact_email"
              label="Email de contact"
              type="email"
              value={formData.contact_email}
              onChange={handleChange}
              fullWidth
              helperText="Email affiché sur la page de contact"
            />
            
            <TextField
              name="contact_phone"
              label="Téléphone de contact"
              value={formData.contact_phone}
              onChange={handleChange}
              fullWidth
              helperText="Numéro de téléphone affiché sur la page de contact"
            />
            
            <TextField
              name="contact_whatsapp"
              label="WhatsApp de contact"
              value={formData.contact_whatsapp}
              onChange={handleChange}
              fullWidth
              helperText="Numéro WhatsApp affiché sur la page de contact"
            />
            
            <TextField
              name="contact_address"
              label="Adresse"
              value={formData.contact_address}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
              helperText="Adresse complète affichée sur la page de contact"
            />
            
            <TextField
              name="contact_opening_hours"
              label="Heures d'ouverture"
              value={formData.contact_opening_hours}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              helperText="Exemple: Lundi - Vendredi: 8h00 - 18h00\nSamedi: 9h00 - 16h00\nDimanche: Fermé"
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PlatformSettingsModal;
